import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import type { IOrgProjectPlatformBreakdown } from '@/feature/organization/types/orgProject';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import Modal from '@/shared/components/ui/Modals/Modal';
import StatCard from '@/shared/components/ui/StatCard';

const PLATFORM_LABELS: Record<string, string> = {
  web: 'Web',
  api: 'API',
  android_mobile: 'Android Mobile',
  ios_mobile: 'iOS Mobile',
  android_browser: 'Android Browser',
  ios_browser: 'iOS Browser',
  manual: 'Manual',
  agent: 'Agent',
};

const formatPlatformLabel = (key: string) =>
  PLATFORM_LABELS[key] ||
  key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

interface IPlatformRow {
  platform: string;
  label: string;
  count: number;
}

interface IProjectPlatformBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  projectName: string;
  breakdown?: IOrgProjectPlatformBreakdown | null;
  total: number;
}

const ProjectPlatformBreakdownModal = ({
  isOpen,
  onClose,
  title,
  projectName,
  breakdown,
  total,
}: IProjectPlatformBreakdownModalProps) => {
  const rows = useMemo<IPlatformRow[]>(() => {
    return Object.entries(breakdown || {})
      .map(([platform, count]) => {
        const safeCount = typeof count === 'number' && Number.isFinite(count) ? count : 0;
        return {
          platform,
          label: formatPlatformLabel(platform),
          count: safeCount,
        };
      })
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [breakdown]);

  const columns = useMemo<ColumnDef<IPlatformRow>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Platform',
        cell: (info) => <span className='text-gray-900'>{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'count',
        header: 'Test cases',
        meta: { width: '140px', align: 'right' as const, noTruncate: true },
        cell: (info) => (
          <span className='font-medium tabular-nums text-gray-900'>
            {info.getValue() as number}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size='lg'>
      <div className='p-6'>
        <p className='mt-0 mb-5 text-sm text-gray-600'>
          Platform classification for{' '}
          <span className='font-semibold text-gray-900'>{projectName}</span>.
        </p>

        <div className='mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3'>
          <StatCard value={total} label='Total test cases' />
        </div>

        <TanStackTable<IPlatformRow>
          data={rows}
          columns={columns}
          enableSearch={false}
          enablePagination={false}
          minHeight='8rem'
          emptyMessage='No platform breakdown available.'
          getRowId={(row) => row.platform}
        />
      </div>
    </Modal>
  );
};

export default ProjectPlatformBreakdownModal;
